export const EXPORT_FAILURE_CODES = new Set([
  'slides_overflow', 'tiles_timeout', 'tiles_failed', 'font_load_failed',
  'font_decode_failed', 'image_decode_failed', 'canvas_security', 'canvas_unavailable',
  'map_center_missing', 'map_resolution_missing', 'map_viewport_missing',
  'map_marker_zoom_missing', 'map_frame_zoom_missing', 'map_print_size_missing', 'map_print_layers_missing',
  'map_not_ready', 'satellite_attribution_failed', 'satellite_region_restricted',
  'cancelled', 'unknown',
] as const);
export type ExportFailureCode = typeof EXPORT_FAILURE_CODES extends Set<infer Code> ? Code : never;

export const EXPORT_TILE_DIAGNOSTIC_PROPS = {
  basemap: new Set(['dark', 'carto', 'landscape', 'osm', 'opentopo', 'satellite', 'hybrid', 'plain', 'unknown']),
  capture_renderer: new Set(['openlayers', 'maplibre']),
  tiles_pending: /^[0-9]{1,5}$/,
  tiles_failed: /^[0-9]{1,5}$/,
  tile_sources: /^[0-9]{1,5}$/,
  render_complete: new Set(['true', 'false']),
  tiles_loaded: new Set(['true', 'false']),
  preview_phase: /* @__PURE__ */ new Set(['render', 'create', 'viewport', 'layout']),
  error_kind: /* @__PURE__ */ new Set(['type', 'range', 'security', 'abort', 'error', 'non_error', 'layout_overflow']),
  tile_http_status: /^(?:[1-5][0-9]{2}|unavailable)$/,
  tiles_cached_at_start: /^[0-9]{1,5}$/,
};
interface ExportTileDiagnostics {
  basemap?: string;
  capture_renderer?: string;
  tiles_pending?: string;
  tiles_failed?: string;
  tile_sources?: string;
  render_complete?: string;
  tiles_loaded?: string;
  preview_phase?: string;
  error_kind?: string;
  tile_http_status?: string;
  tiles_cached_at_start?: string;
}

export function exportBasemapDiagnostic(basemap: string): string {
  return EXPORT_TILE_DIAGNOSTIC_PROPS.basemap.has(basemap) ? basemap : 'unknown';
}

/** Page content cannot fit; this is not a transport or tile failure. */
export class ExportLayoutFailure extends Error {}

export class ExportFailure extends Error {
  constructor(readonly code: ExportFailureCode, message: string, options?: ErrorOptions, readonly diagnostics?: ExportTileDiagnostics) {
    super(message, options);
    this.name = 'ExportFailure';
  }
}

export function exportFailureDiagnostics(cause: unknown): ExportTileDiagnostics {
  if (!(cause instanceof ExportFailure) || !cause.diagnostics) return {};
  const result: ExportTileDiagnostics = {};
  // SAFETY: These keys come from the fixed diagnostic allowlist.
  for (const key of Object.keys(EXPORT_TILE_DIAGNOSTIC_PROPS) as Array<keyof ExportTileDiagnostics>) {
    const value = cause.diagnostics[key];
    const rule = EXPORT_TILE_DIAGNOSTIC_PROPS[key];
    if (value !== undefined && (rule instanceof Set ? rule.has(value) : rule.test(value))) result[key] = value;
  }
  return result;
}

export function exportFailureCode(cause: unknown): ExportFailureCode {
  if (cause instanceof ExportFailure) return cause.code;
  if (cause instanceof Error && cause.name === 'SecurityError') return 'canvas_security';
  if (cause instanceof Error && cause.name === 'AbortError') return 'cancelled';
  return 'unknown';
}
